/**
 * The BEST MISSIONS list screen — split from `missions-screens.ts` when the
 * squadron-fight merge pushed that file past the size gate (the established
 * sibling-module pattern). Same widget shape as VIEW TOP ACES via
 * `panel-parts.ts`'s `listScreen`.
 */

import type { BestMissionEntry } from "@red-opus/engine";
import type { FontAtlas } from "../render/text-metrics.ts";
import { listScreen } from "./panel-parts.ts";
import type { ListRow, ShellScreen } from "./shell-screen.ts";

/**
 * `BEST MISSIONS` — the same list-box widget `VIEW TOP ACES` uses
 * (`panel-parts.ts`'s `listScreen`), with the two columns `PS.EXE`'s string
 * pool actually offers a single mission: `PLANE` / `SCORE` (see
 * `best-missions.ts`'s module note for the evidence — a career gets `KILLS`,
 * a single mission has no kill tally of its own, only the aeroplane it was
 * flown in). The name typed in at scoring time joins the plane in the label
 * column, since the list row has no third slot. `PS.EXE`'s own empty-table
 * line, verbatim: `"The best mission list is empty. Please play a mission."`
 * `EXIT` is the original's own button (its sibling `VIEW` opened a detail
 * screen this port does not build — nothing here is hidden behind it, so it
 * would be a button to nowhere).
 */
export function bestMissionsScreen(entries: readonly BestMissionEntry[], font: FontAtlas): ShellScreen {
  const rows: ListRow[] = entries.map((entry, index) => ({
    id: `${index}`,
    label: `${entry.name}  ${entry.plane}`,
    trailing: `${entry.score}`,
  }));
  const prompt = rows.length > 0 ? "BEST MISSIONS:" : "THE BEST MISSION LIST IS EMPTY. PLEASE PLAY A MISSION.";
  return listScreen(
    {
      id: "missions/best",
      title: "BEST MISSIONS",
      prompt,
      rows,
      actions: [{ id: "exit", label: "EXIT", action: { kind: "command" as const, command: "grid" } }],
    },
    { listIndex: 0, listTop: 0 },
    font,
  );
}
